import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { createTenantService } from './onboardingWizardServices'
import { SelectDropdown } from '../../components/SelectDropdown'
import toast from 'react-hot-toast'
import {
  TIERS,
  defaultPolicyForTier,
  payoutPolicyFromVenueRow,
  formatTierRange,
} from '../payout-safe/payoutPolicyUtils'
import {
  BuildingOfficeIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MapPinIcon,
  CalendarDaysIcon,
  CogIcon,
  CpuChipIcon,
  ClipboardDocumentCheckIcon,
} from '@heroicons/react/24/outline'

const STEPS = [
  { id: 'welcome', title: 'Welcome', icon: ClipboardDocumentCheckIcon },
  { id: 'business', title: 'Business profile', icon: BuildingOfficeIcon },
  { id: 'venues', title: 'Venues & sites', icon: MapPinIcon },
  { id: 'trading', title: 'Trading hours', icon: CalendarDaysIcon },
  { id: 'policy', title: 'Policy & limits', icon: CogIcon },
  { id: 'egms', title: 'EGMs', icon: CpuChipIcon },
  { id: 'review', title: 'Review', icon: CheckCircleIcon },
]

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']

const SHARED_VENUE_KEYS = [
  'venue_phone',
  'venue_email',
  'license_number',
  'license_expiry',
  'membership_system',
  'gaming_system',
  'visitor_management_system',
]

const emptySharedVenue = () => ({
  venue_phone: '',
  venue_email: '',
  license_number: '',
  license_expiry: '',
  membership_system: '',
  gaming_system: '',
  visitor_management_system: '',
})

const emptyVenue = () => ({
  venue_id: '',
  name: '',
  code: '',
  location: '',
  venue_address: '',
  ...emptySharedVenue(),
  active: false,
})

function pickSharedFromVenue(v) {
  const s = emptySharedVenue()
  for (const k of SHARED_VENUE_KEYS) s[k] = v?.[k] ?? ''
  if (s.license_expiry) s.license_expiry = String(s.license_expiry).slice(0, 10)
  return s
}

function applySharedToVenue(v, shared) {
  const out = { ...v }
  for (const k of SHARED_VENUE_KEYS) out[k] = shared[k] ?? ''
  return out
}

function venuesForSave(venues, shared) {
  return venues.map((v) => applySharedToVenue(v, shared))
}

const emptyEgm = () => ({ egm_id: '', venue_id: '', house_number: '', active: true })

const DEFAULT_OPERATIONAL = {
  payout: {
    default_authorization_threshold: '5000',
    manager_approval_threshold: '10000',
    dual_approval_threshold: '25000',
    daily_payout_limit: '50000',
    require_id_verification_above: '2000',
  },
  kyc: {
    kyc_mode: 'on_expiry',
    id_expiry_warning_days: '90',
    reverification_period_months: '12',
    max_failed_verification_attempts: '3',
    cooldown_after_failed_hours: '24',
    desktop_biometric_mode: 'required',
    multi_document_enabled: 'false',
  },
  document_processing: {
    retain_license_image_after_ocr: 'true',
    id_image_retention_days: '2555',
    evidence_image_retention_days: '2555',
  },
  aml_thresholds: {
    daily_cash_limit: '25000',
    structuring_total_amount: '25000',
    structuring_min_transactions: '3',
  },
  risk_scoring: {
    critical_threshold: '40',
    high_threshold: '60',
    medium_threshold: '80',
    low_threshold: '100',
  },
  alert_policy: {
    suppression_window_hours: '168',
    dedup_window_hours: '24',
  },
  reporting: {
    compliance_officer_name: '',
    compliance_officer_position: '',
    compliance_officer_email: '',
    compliance_officer_phone: '',
  },
  transaction_risk: {
    amount_band_low_threshold: '2000', amount_band_low_points: '10',
    amount_band_mid_threshold: '5000', amount_band_mid_points: '22',
    amount_band_high_threshold: '10000', amount_band_high_points: '26',
    cash_ratio_low_threshold: '50', cash_ratio_low_points: '6',
    cash_ratio_mid_threshold: '70', cash_ratio_mid_points: '12',
    cash_ratio_high_threshold: '90', cash_ratio_high_points: '18',
    cash_component_cap: '5000', cash_component_cap_points: '16',
    cash_component_near_cap: '4500', cash_component_near_cap_points: '8',
    tier_pv3_points: '6', tier_pv4_points: '10',
    win_type_jackpot_points: '8',
    level_high_threshold: '70', level_medium_threshold: '40',
    investigate_threshold: '50',
    patron_high_combined_patron_score: '60',
    patron_high_combined_txn_score: '35',
  },
}

const PAYOUT_LABELS = {
  default_authorization_threshold: 'Cashier approval from',
  manager_approval_threshold: 'Manager approval from',
  dual_approval_threshold: 'Dual approval from',
  daily_payout_limit: 'Daily payout cap',
  require_id_verification_above: 'ID verification above',
}

const DOC_PROCESSING_LABELS = {
  id_image_retention_days: 'ID image retention (days)',
  evidence_image_retention_days: 'Evidence image retention (days)',
}

const AML_LABELS = {
  daily_cash_limit: 'Daily cash limit (AUD)',
  structuring_total_amount: 'Structuring total amount (AUD)',
  structuring_min_transactions: 'Structuring min transactions',
}

const RISK_LABELS = {
  critical_threshold: 'Critical risk score ≤',
  high_threshold: 'High risk score ≤',
  medium_threshold: 'Medium risk score ≤',
  low_threshold: 'Low risk score ≤',
}

const ALERT_LABELS = {
  suppression_window_hours: 'Suppression window (hours)',
  dedup_window_hours: 'Dedup window (hours)',
}

const REPORTING_LABELS = {
  compliance_officer_name: 'Compliance officer name',
  compliance_officer_position: 'Position / title',
  compliance_officer_email: 'Email',
  compliance_officer_phone: 'Phone',
}

const TXN_RISK_GROUPS = [
  { title: 'Amount bands (AUD → points)', fields: [
    ['amount_band_low_threshold', 'Low threshold ($)'],
    ['amount_band_low_points', 'Low points'],
    ['amount_band_mid_threshold', 'Mid threshold ($)'],
    ['amount_band_mid_points', 'Mid points'],
    ['amount_band_high_threshold', 'High threshold ($)'],
    ['amount_band_high_points', 'High points'],
  ] },
  { title: 'Cash ratio bands (% → points)', fields: [
    ['cash_ratio_low_threshold', 'Low % threshold'],
    ['cash_ratio_low_points', 'Low points'],
    ['cash_ratio_mid_threshold', 'Mid % threshold'],
    ['cash_ratio_mid_points', 'Mid points'],
    ['cash_ratio_high_threshold', 'High % threshold'],
    ['cash_ratio_high_points', 'High points'],
  ] },
  { title: 'Cash component (desk cap)', fields: [
    ['cash_component_cap', 'Desk cap ($)'],
    ['cash_component_cap_points', 'At-cap points'],
    ['cash_component_near_cap', 'Near-cap ($)'],
    ['cash_component_near_cap_points', 'Near-cap points'],
  ] },
  { title: 'Tier & win-type signals', fields: [
    ['tier_pv3_points', 'PV3 ($5k–$10k) points'],
    ['tier_pv4_points', 'PV4 ($10k+) points'],
    ['win_type_jackpot_points', 'Jackpot/major prize points'],
  ] },
  { title: 'Cutoffs & investigate trigger', fields: [
    ['level_high_threshold', 'HIGH level cutoff (score ≥)'],
    ['level_medium_threshold', 'MEDIUM level cutoff (score ≥)'],
    ['investigate_threshold', '"Suggest investigate" score'],
    ['patron_high_combined_patron_score', 'Combined trigger — patron score ≥'],
    ['patron_high_combined_txn_score', 'Combined trigger — txn score ≥'],
  ] },
]

/** Per-category overlay of saved values on DEFAULT_OPERATIONAL. */
function mergeOperationalDefaults(incoming) {
  const out = {}
  for (const cat of Object.keys(DEFAULT_OPERATIONAL)) {
    out[cat] = { ...DEFAULT_OPERATIONAL[cat], ...((incoming && incoming[cat]) || {}) }
  }
  return out
}

const KYC_MODE_OPTIONS = [
  { value: 'on_expiry', label: 'When ID expires' },
  { value: 'always', label: 'Every payout' },
  { value: 'never', label: 'Never (not recommended)' },
]

function formatSettingLabel(key) {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())
}

const TIER_ONBOARDING_LABELS = {
  PV1: 'Tier 1 — Low value',
  PV2: 'Tier 2 — Standard',
  PV3: 'Tier 3 — High value',
  PV4: 'Tier 4 — Reportable',
}

function emptySharedPayoutPolicy() {
  const tiers = {}
  for (const tid of TIERS) {
    const d = defaultPolicyForTier(tid)
    tiers[tid] = {
      enabled: d.enabled !== false,
      minAmount: d.minAmount ?? 0,
      maxAmount: d.maxAmount ?? null,
      kycRequired: !!d.kycRequired,
    }
  }
  return { tiers, cashLimit: 5000, cashLimitEnforced: true }
}

function pickSharedPayoutFromVenue(v) {
  if (!v?.payout_policy) return emptySharedPayoutPolicy()
  const full = payoutPolicyFromVenueRow(v)
  const tiers = {}
  for (const tid of TIERS) {
    const t = full[tid] || {}
    tiers[tid] = {
      enabled: t.enabled !== false,
      minAmount: t.minAmount ?? 0,
      maxAmount: t.maxAmount ?? null,
      kycRequired: !!t.kycRequired,
    }
  }
  let cashLimit = 5000
  try {
    const raw = typeof v.payout_policy === 'object' ? v.payout_policy : JSON.parse(v.payout_policy)
    if (raw?.cashLimit != null) cashLimit = Number(raw.cashLimit)
  } catch (_) {}
  return { tiers, cashLimit, cashLimitEnforced: true }
}

function mergeSharedPayoutDraft(incoming) {
  const base = emptySharedPayoutPolicy()
  if (!incoming) return base
  return {
    cashLimit: incoming.cashLimit ?? base.cashLimit,
    cashLimitEnforced: incoming.cashLimitEnforced !== false,
    tiers: TIERS.reduce((acc, tid) => {
      acc[tid] = { ...base.tiers[tid], ...(incoming.tiers?.[tid] || {}) }
      return acc
    }, {}),
  }
}

function slugVsid(name) {
  return String(name || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 32)
}

const UNIT_STYLES = {
  AUD:    'bg-emerald-100 text-emerald-700',
  days:   'bg-sky-100 text-sky-700',
  months: 'bg-blue-100 text-blue-700',
  hours:  'bg-cyan-100 text-cyan-700',
  '%':    'bg-violet-100 text-violet-700',
  points: 'bg-amber-100 text-amber-700',
  score:  'bg-yellow-100 text-yellow-800',
  count:  'bg-gray-100 text-gray-700',
}

function UnitChip({ unit }) {
  if (!unit) return null
  const cls = UNIT_STYLES[unit] || 'bg-gray-100 text-gray-700'
  return <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${cls}`}>{unit}</span>
}

/** Centralised "what unit is this field?" so the wizard chip matches reality. */
function unitOf(category, key) {
  if (category === 'payout') return 'AUD'
  if (category === 'kyc') {
    if (key === 'kyc_mode') return null
    if (key.endsWith('_days')) return 'days'
    if (key.endsWith('_months')) return 'months'
    if (key.endsWith('_hours')) return 'hours'
    return 'count'
  }
  if (category === 'document_processing') {
    if (key === 'retain_license_image_after_ocr') return null
    return 'days'
  }
  if (category === 'aml_thresholds') {
    if (key === 'structuring_min_transactions') return 'count'
    return 'AUD'
  }
  if (category === 'risk_scoring') return 'score'
  if (category === 'alert_policy') return 'hours'
  if (category === 'transaction_risk') {
    if (key.endsWith('_points')) return 'points'
    if (key.startsWith('cash_ratio_') && key.endsWith('_threshold')) return '%'
    if (key.startsWith('amount_band_') && key.endsWith('_threshold')) return 'AUD'
    if (key === 'cash_component_cap' || key === 'cash_component_near_cap') return 'AUD'
    if (key.startsWith('level_') || key === 'investigate_threshold' || key.startsWith('patron_high_combined_')) return 'score'
    return null
  }
  return null
}

function Field({ label, required, children, hint, unit }) {
  return (
    <div>
      <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
        <span>{label}</span>
        {required && <span className="text-red-500 ml-0.5">*</span>}
        <UnitChip unit={unit} />
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-gray-500">{hint}</p>}
    </div>
  )
}

export default function ClubOnboardingWizard({ service: serviceProp } = {}) {
  // Default to the tenant-side service. Platform-admin wrapper passes
  // createPlatformAdminService(setupId) instead.
  const service = serviceProp || createTenantService()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [stepIndex, setStepIndex] = useState(0)
  const [status, setStatus] = useState('incomplete')
  const [groupId, setGroupId] = useState('')

  const [business, setBusiness] = useState({
    legal_name: '',
    name: '',
    abn: '',
    acn: '',
    austrac_id: '',
    registered_address: '',
    business_phone: '',
    business_email: '',
    timezone: 'Australia/Sydney',
    currency: 'AUD',
    jurisdiction: 'NSW',
    headquarters: '',
    groupCode: '',
  })

  const [sharedVenue, setSharedVenue] = useState(emptySharedVenue())
  const [venues, setVenues] = useState([emptyVenue()])
  const [weeklyTrading, setWeeklyTrading] = useState({})
  const [groupSettings, setGroupSettings] = useState({
    defaultLimits: { maxCashPayout: 5000, maxDailyPayout: 15000, maxSingleTransaction: 10000 },
    complianceSettings: { amlMonitoringEnabled: true, smrAutoDraftEnabled: true, ecddWorkflowEnabled: true },
    reportingConfig: { smrReportingEnabled: true, ttrReportingEnabled: false, ctrReportingEnabled: false },
  })
  const [operationalConfig, setOperationalConfig] = useState(() => mergeOperationalDefaults(null))
  const [sharedPayoutPolicy, setSharedPayoutPolicy] = useState(emptySharedPayoutPolicy)
  const [egms, setEgms] = useState([])
  const [activateVenueIds, setActivateVenueIds] = useState([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const j = await service.load()
      if (!j.success) throw new Error(j.error || 'Failed to load')
      const d = j.data || {}
      setStatus(d.status || 'incomplete')
      setGroupId(d.groupId || user?.group_id || '')
      if (d.step > 0 && d.step < STEPS.length) setStepIndex(d.step)

      const b = d.business || {}
      const gi = d.groupInfo || {}
      setBusiness({
        legal_name: b.legal_name || gi.legalName || '',
        name: b.name || gi.groupName || '',
        abn: b.abn || gi.abn || '',
        acn: b.acn || gi.acn || '',
        austrac_id: b.austrac_id || gi.austracId || '',
        registered_address: b.registered_address || gi.registeredAddress || '',
        business_phone: b.business_phone || gi.businessPhone || '',
        business_email: b.business_email || gi.businessEmail || '',
        timezone: gi.timezone || 'Australia/Sydney',
        currency: gi.currency || 'AUD',
        jurisdiction: gi.jurisdiction || 'NSW',
        headquarters: gi.headquarters || '',
        groupCode: gi.groupCode || '',
      })

      if (d.venues?.length) {
        const mapped = d.venues.map((v) => ({
          venue_id: v.venue_id || '',
          name: v.name || '',
          code: v.code || '',
          location: v.location || '',
          venue_address: v.venue_address || '',
          venue_phone: v.venue_phone || '',
          venue_email: v.venue_email || '',
          license_number: v.license_number || '',
          license_expiry: v.license_expiry ? String(v.license_expiry).slice(0, 10) : '',
          membership_system: v.membership_system || '',
          gaming_system: v.gaming_system || '',
          visitor_management_system: v.visitor_management_system || '',
          active: !!v.active,
        }))
        setVenues(mapped)
        setSharedVenue(pickSharedFromVenue(mapped[0]))
        const withPolicy = d.venues.find((v) => v.payout_policy)
        if (withPolicy) setSharedPayoutPolicy(pickSharedPayoutFromVenue(withPolicy))
        setActivateVenueIds(d.venues.filter((v) => v.active).map((v) => v.venue_id))
      }

      if (d.sharedPayoutPolicy) setSharedPayoutPolicy(mergeSharedPayoutDraft(d.sharedPayoutPolicy))

      if (d.groupSettings) setGroupSettings(d.groupSettings)
      if (d.operationalConfig) {
        setOperationalConfig(mergeOperationalDefaults(d.operationalConfig))
      }
      if (d.defaultWeekly) setWeeklyTrading(d.defaultWeekly)
      if (d.egms?.length) {
        setEgms(d.egms.map((e) => ({
          egm_id: e.egm_id || '',
          venue_id: e.venue_id || '',
          house_number: e.house_number || '',
          active: e.active !== false,
        })))
      }

      if (d.draft?.sharedVenue) setSharedVenue({ ...emptySharedVenue(), ...d.draft.sharedVenue })
      if (d.draft?.venues?.length) {
        setVenues(d.draft.venues)
        if (!d.draft?.sharedVenue) setSharedVenue(pickSharedFromVenue(d.draft.venues[0]))
      }
      if (d.draft?.business) setBusiness((prev) => ({ ...prev, ...d.draft.business }))
      if (d.draft?.weeklyTrading) setWeeklyTrading(d.draft.weeklyTrading)
      if (d.draft?.groupSettings) setGroupSettings(d.draft.groupSettings)
      if (d.draft?.operationalConfig) {
        setOperationalConfig(mergeOperationalDefaults(d.draft.operationalConfig))
      }
      if (d.draft?.sharedPayoutPolicy) setSharedPayoutPolicy(mergeSharedPayoutDraft(d.draft.sharedPayoutPolicy))
      if (d.draft?.egms) setEgms(d.draft.egms)
    } catch (e) {
      toast.error(e.message || 'Could not load onboarding')
    } finally {
      setLoading(false)
    }
  }, [user?.group_id])

  useEffect(() => { load() }, [load])

  // Platform-admin: keep pending-setups list title in sync while typing (no Next/Save required).
  useEffect(() => {
    if (service.mode !== 'platform-admin' || loading || !service.syncListLabel) return undefined
    const display = (business.name || business.legal_name || '').trim()
    const slug = String(business.groupCode || '').trim()
    if (!display && !slug) return undefined
    const timer = setTimeout(() => {
      service.syncListLabel(business).catch(() => {})
    }, 1200)
    return () => clearTimeout(timer)
  }, [business.name, business.legal_name, business.groupCode, service, loading])

  const buildDraftPayload = () => ({
    business,
    sharedVenue,
    venues,
    weeklyTrading,
    groupSettings,
    operationalConfig,
    sharedPayoutPolicy,
    egms,
    activateVenueIds,
  })

  const saveDraft = async (nextStep) => {
    return service.saveDraft({
      step: nextStep,
      draft: buildDraftPayload(),
    })
  }

  const handleSaveDraft = async () => {
    setSaving(true)
    try {
      await saveDraft(stepIndex)
      toast.success('Draft saved')
    } catch (e) {
      toast.error(e.message || 'Failed to save draft')
    } finally {
      setSaving(false)
    }
  }

  const persistBusiness = async () => {
    const j = await service.saveBusiness(business)
    if (!j.success) throw new Error(j.error || 'Failed to save business profile')
  }

  const persistPolicy = async () => {
    const j = await service.savePolicy({
      groupSettings,
      operationalConfig,
      sharedPayoutPolicy,
      groupInfo: { groupName: business.name, gsid: groupId },
    })
    if (!j.success) throw new Error(j.error || 'Failed to save policy')
  }

  const validateBusiness = () => {
    if (!business.legal_name?.trim()) return 'Legal name is required'
    if (!business.abn?.trim()) return 'ABN is required'
    if (!business.austrac_id?.trim()) return 'AUSTRAC reporting entity ID is required'
    if (!business.registered_address?.trim()) return 'Registered address is required'
    if (!business.business_email?.trim()) return 'Compliance email is required'
    return null
  }

  const validateVenues = () => {
    if (!sharedVenue.venue_phone?.trim()) return 'Venue phone is required (shared for all sites)'
    if (!sharedVenue.venue_email?.trim()) return 'Venue email is required (shared for all sites)'
    if (!sharedVenue.license_number?.trim()) return 'Gaming licence number is required (shared for all sites)'
    if (!sharedVenue.license_expiry?.trim()) return 'Licence expiry is required (shared for all sites)'
    for (let i = 0; i < venues.length; i++) {
      const v = venues[i]
      if (!v.venue_id?.trim() || !v.name?.trim()) return `Site ${i + 1}: ID and name are required`
      if (!v.venue_address?.trim()) return `Site ${i + 1}: street address is required`
    }
    return null
  }

  const setSharedVenueField = (field, value) => {
    setSharedVenue((s) => ({ ...s, [field]: value }))
    setVenues((prev) => prev.map((v) => ({ ...v, [field]: value })))
  }

  const goNext = async () => {
    const step = STEPS[stepIndex].id
    if (step === 'business') {
      const err = validateBusiness()
      if (err) { toast.error(err); return }
      setSaving(true)
      try {
        await persistBusiness()
        await saveDraft(stepIndex + 1)
        setStepIndex((i) => i + 1)
      } catch (e) {
        toast.error(e.message)
      } finally {
        setSaving(false)
      }
      return
    }
    if (step === 'venues') {
      const err = validateVenues()
      if (err) { toast.error(err); return }
    }
    if (step === 'policy') {
      setSaving(true)
      try {
        await persistPolicy()
      } catch (e) {
        toast.error(e.message)
        setSaving(false)
        return
      }
      setSaving(false)
    }
    setSaving(true)
    try {
      await saveDraft(stepIndex + 1)
      setStepIndex((i) => Math.min(i + 1, STEPS.length - 1))
    } catch (e) {
      toast.error(e.message || 'Failed to save draft')
    } finally {
      setSaving(false)
    }
  }

  const goBack = () => {
    setStepIndex((i) => Math.max(0, i - 1))
  }

  const finish = async () => {
    const err = validateBusiness() || validateVenues()
    if (err) { toast.error(err); return }
    setSaving(true)
    try {
      const j = await service.complete({
        business,
        venues: venuesForSave(venues, sharedVenue),
        groupSettings,
        operationalConfig,
        sharedPayoutPolicy,
        weeklyTrading,
        activateVenueIds,
        egms,
      })
      if (!j.success) throw new Error(j.error || 'Failed to complete onboarding')
      setStatus('complete')
      toast.success('Club onboarding complete')
      navigate('/admin/group-config')
    } catch (e) {
      toast.error(e.message || 'Could not complete onboarding')
    } finally {
      setSaving(false)
    }
  }

  const updateVenue = (idx, field, value) => {
    setVenues((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], [field]: value }
      if (field === 'name' && !next[idx].venue_id) {
        next[idx].venue_id = slugVsid(value)
      }
      return next
    })
  }

  const step = STEPS[stepIndex]

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center text-gray-600">
        Loading club setup…
      </div>
    )
  }

  if (status === 'complete' && stepIndex === 0) {
    return (
      <div className="max-w-2xl mx-auto py-12 space-y-6 text-center">
        <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto" />
        <h1 className="text-2xl font-bold text-gray-900">Club setup complete</h1>
        <p className="text-gray-600">
          Your business, venue, and policy details are saved. PaySafe will complete technical provisioning separately.
        </p>
        <div className="flex justify-center gap-3">
          <Link to="/admin/group-config" className="px-4 py-2 bg-psg-teal text-white rounded-md hover:opacity-90">
            Group Configuration
          </Link>
          <button type="button" onClick={() => { setStepIndex(1); setStatus('incomplete') }} className="px-4 py-2 border rounded-md text-gray-700">
            Review again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto pb-16">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Club setup wizard</h1>
        <p className="mt-1 text-sm text-gray-600">
          Provide your organisation&apos;s business, legal, venue, and policy details. PaySafe handles all technical setup (hosting, integrations, devices).
        </p>
        {(service.mode === 'platform-admin'
          ? (business.name || business.legal_name || '').trim()
          : groupId) && (
          <p className="mt-1 text-xs text-gray-500 font-mono">
            Organisation:{' '}
            {service.mode === 'platform-admin'
              ? (business.name || business.legal_name)
              : groupId}
          </p>
        )}
      </div>

      <div className="lg:flex lg:gap-6">
        {/* Left sidebar — wizard steps (vertical on lg+, horizontal scroll on mobile) */}
        <aside className="lg:w-60 lg:shrink-0 mb-4 lg:mb-0">
          <nav className="lg:sticky lg:top-4 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible border-b lg:border-b-0 lg:border-r border-gray-200 lg:pr-3 pb-2 lg:pb-0">
            {STEPS.map((s, i) => {
              const isActive = i === stepIndex
              const isDone = i < stepIndex
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setStepIndex(i)}
                  className={`group inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap lg:whitespace-normal text-left ${
                    isActive ? 'bg-psg-teal text-white'
                    : isDone ? 'bg-teal-50 text-teal-800 hover:bg-teal-100'
                    : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <s.icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1">{s.title}</span>
                  {isDone && <CheckCircleIcon className="h-3.5 w-3.5 shrink-0 text-teal-600 hidden lg:inline" />}
                </button>
              )
            })}
          </nav>
        </aside>

        {/* Right column — content + bottom nav */}
        <div className="flex-1 min-w-0 space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 min-h-[320px]">
        {step.id === 'welcome' && (
          <div className="space-y-4 text-sm text-gray-700">
            <p>This wizard collects information <strong>your club</strong> must provide:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Legal entity and AUSTRAC reporting details</li>
              <li>Each venue site (licence, address, contacts)</li>
              <li>Trading hours, payout tiers (PV1–PV4), limits, compliance, and KYC defaults</li>
              <li>EGM registry (optional now; can add later in Group Configuration)</li>
            </ul>
            <p className="text-gray-500">
              You will not be asked for Azure keys, Service Bus, or gateway configuration — that is handled by PaySafe.
            </p>
          </div>
        )}

        {step.id === 'business' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Legal name" required>
              <input className="input-field w-full" value={business.legal_name} onChange={(e) => setBusiness((b) => ({ ...b, legal_name: e.target.value }))} />
            </Field>
            <Field label="Trading / display name">
              <input className="input-field w-full" value={business.name} onChange={(e) => setBusiness((b) => ({ ...b, name: e.target.value }))} />
            </Field>
            <Field label="ABN" required>
              <input className="input-field w-full" value={business.abn} onChange={(e) => setBusiness((b) => ({ ...b, abn: e.target.value }))} />
            </Field>
            <Field label="AUSTRAC reporting entity ID" required hint="One ID for the whole club (tenant), not per venue — SMR field A2">
              <input className="input-field w-full font-mono" value={business.austrac_id} onChange={(e) => setBusiness((b) => ({ ...b, austrac_id: e.target.value }))} />
            </Field>
            <Field label="ACN">
              <input className="input-field w-full" value={business.acn} onChange={(e) => setBusiness((b) => ({ ...b, acn: e.target.value }))} />
            </Field>
            <div className="md:col-span-2">
              <Field label="Registered address" required>
                <textarea className="input-field w-full" rows={2} value={business.registered_address} onChange={(e) => setBusiness((b) => ({ ...b, registered_address: e.target.value }))} />
              </Field>
            </div>
            <Field label="Compliance phone" required>
              <input className="input-field w-full" value={business.business_phone} onChange={(e) => setBusiness((b) => ({ ...b, business_phone: e.target.value }))} />
            </Field>
            <Field label="Compliance email" required>
              <input type="email" className="input-field w-full" value={business.business_email} onChange={(e) => setBusiness((b) => ({ ...b, business_email: e.target.value }))} />
            </Field>
            <Field label="Timezone">
              <SelectDropdown
                className="w-full"
                value={business.timezone}
                onChange={(e) => setBusiness((b) => ({ ...b, timezone: e.target.value }))}
                options={[
                  { value: 'Australia/Sydney', label: 'Australia/Sydney' },
                  { value: 'Australia/Melbourne', label: 'Australia/Melbourne' },
                  { value: 'Australia/Brisbane', label: 'Australia/Brisbane' },
                  { value: 'Australia/Perth', label: 'Australia/Perth' },
                ]}
              />
            </Field>
            <Field label="Jurisdiction">
              <input className="input-field w-full" value={business.jurisdiction} onChange={(e) => setBusiness((b) => ({ ...b, jurisdiction: e.target.value }))} />
            </Field>
          </div>
        )}

        {step.id === 'venues' && (
          <div className="space-y-6">
            <div className="rounded-lg border border-teal-200 bg-teal-50/50 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-teal-900">Shared across all venues</h3>
              <p className="text-xs text-teal-800">
                Phone, email, licence, and systems are usually the same for every site — enter once; we copy to each venue on save.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Venue phone" required>
                  <input className="input-field w-full" value={sharedVenue.venue_phone} onChange={(e) => setSharedVenueField('venue_phone', e.target.value)} />
                </Field>
                <Field label="Venue email" required>
                  <input type="email" className="input-field w-full" value={sharedVenue.venue_email} onChange={(e) => setSharedVenueField('venue_email', e.target.value)} />
                </Field>
                <Field label="Gaming licence number" required>
                  <input className="input-field w-full" value={sharedVenue.license_number} onChange={(e) => setSharedVenueField('license_number', e.target.value)} />
                </Field>
                <Field label="Licence expiry" required>
                  <input type="date" className="input-field w-full" value={sharedVenue.license_expiry} onChange={(e) => setSharedVenueField('license_expiry', e.target.value)} />
                </Field>
                <Field label="Membership system" hint="e.g. IGT — facts only, not credentials">
                  <input className="input-field w-full" value={sharedVenue.membership_system} onChange={(e) => setSharedVenueField('membership_system', e.target.value)} />
                </Field>
                <Field label="Gaming system">
                  <input className="input-field w-full" value={sharedVenue.gaming_system} onChange={(e) => setSharedVenueField('gaming_system', e.target.value)} />
                </Field>
                <Field label="Visitor management">
                  <input className="input-field w-full" value={sharedVenue.visitor_management_system} onChange={(e) => setSharedVenueField('visitor_management_system', e.target.value)} />
                </Field>
              </div>
            </div>

            <p className="text-sm font-medium text-gray-900">Sites (unique per venue)</p>
            {venues.map((v, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <h3 className="font-medium text-gray-900">Site {idx + 1}</h3>
                  {venues.length > 1 && (
                    <button type="button" className="text-sm text-red-600" onClick={() => setVenues((prev) => prev.filter((_, i) => i !== idx))}>
                      Remove
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Venue ID (VSID)" required hint="Agree with PaySafe if unsure">
                    <input className="input-field w-full font-mono" value={v.venue_id} onChange={(e) => updateVenue(idx, 'venue_id', e.target.value.toUpperCase())} />
                  </Field>
                  <Field label="Venue name" required>
                    <input className="input-field w-full" value={v.name} onChange={(e) => updateVenue(idx, 'name', e.target.value)} />
                  </Field>
                  <Field label="Location">
                    <input className="input-field w-full" value={v.location} onChange={(e) => updateVenue(idx, 'location', e.target.value)} />
                  </Field>
                  <Field label="Short code">
                    <input className="input-field w-full" value={v.code} onChange={(e) => updateVenue(idx, 'code', e.target.value)} />
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Street address" required>
                      <input className="input-field w-full" value={v.venue_address} onChange={(e) => updateVenue(idx, 'venue_address', e.target.value)} />
                    </Field>
                  </div>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="text-sm text-psg-teal font-medium"
              onClick={() => setVenues((prev) => [...prev, applySharedToVenue(emptyVenue(), sharedVenue)])}
            >
              + Add another site
            </button>
          </div>
        )}

        {step.id === 'trading' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Default weekly hours applied to every venue on completion. Adjust per venue later in Group Configuration.</p>
            {DAY_ORDER.map((day) => {
              const current = weeklyTrading[day] || { start: '10:00', end: '03:00+1', enabled: true, nextDay: true }
              return (
                <div key={day} className="flex flex-wrap items-center gap-3 p-2 bg-gray-50 rounded">
                  <span className="w-24 capitalize text-sm font-medium">{day}</span>
                  <label className="text-xs flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={current.enabled}
                      onChange={(e) => setWeeklyTrading((w) => ({ ...w, [day]: { ...current, enabled: e.target.checked } }))}
                    />
                    Open
                  </label>
                  <input
                    type="time"
                    className="text-sm border rounded px-2"
                    value={String(current.start).replace('+1', '')}
                    onChange={(e) => setWeeklyTrading((w) => ({ ...w, [day]: { ...current, start: e.target.value } }))}
                  />
                  <span className="text-xs text-gray-500">to</span>
                  <input
                    type="time"
                    className="text-sm border rounded px-2"
                    value={String(current.end).replace('+1', '')}
                    onChange={(e) => {
                      const endBase = e.target.value
                      setWeeklyTrading((w) => ({
                        ...w,
                        [day]: { ...current, end: current.nextDay ? `${endBase}+1` : endBase },
                      }))
                    }}
                  />
                  <label className="text-xs flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={current.nextDay}
                      onChange={(e) => {
                        const endBase = String(current.end).replace('+1', '')
                        setWeeklyTrading((w) => ({
                          ...w,
                          [day]: { ...current, nextDay: e.target.checked, end: e.target.checked ? `${endBase}+1` : endBase },
                        }))
                      }}
                    />
                    Closes next day
                  </label>
                </div>
              )
            })}
          </div>
        )}

        {step.id === 'policy' && (
          <div className="xl:flex xl:gap-6">
            <div className="flex-1 min-w-0 space-y-8">
            <p className="text-sm text-gray-600">
              Matches <strong>Group Configuration → Group Settings</strong> and key items from{' '}
              <strong>Venue Configuration</strong> (group-wide defaults). Advanced tuning stays in those screens after onboarding.
            </p>

            <div id="policy-payout">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Group transaction limits (AUD)</h3>
              <p className="text-xs text-gray-500 mb-3">UI caps shown in Group Settings — used for dashboards and defaults.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(groupSettings.defaultLimits).map(([key, val]) => (
                  <Field key={key} label={formatSettingLabel(key)}>
                    <input
                      type="number"
                      className="input-field w-full"
                      value={val}
                      onChange={(e) =>
                        setGroupSettings((g) => ({
                          ...g,
                          defaultLimits: { ...g.defaultLimits, [key]: Number(e.target.value) || 0 },
                        }))
                      }
                    />
                  </Field>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-4 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Payout value tiers — all venues (PV1–PV4)</h3>
                <p className="text-xs text-gray-500 mt-1">
                  Same tier brackets and KYC rules for every site. Copied to each venue&apos;s payout policy on save
                  (fine-tune per site later in Payout Rules Builder).
                </p>
              </div>
              <Field label="Max cash payout per transaction (AUD)" hint="Above this, remainder must be EFT">
                <input
                  type="number"
                  className="input-field w-full max-w-xs"
                  value={sharedPayoutPolicy.cashLimit}
                  onChange={(e) =>
                    setSharedPayoutPolicy((s) => ({ ...s, cashLimit: Number(e.target.value) || 0 }))
                  }
                />
              </Field>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm border border-gray-200 rounded-md">
                  <thead className="bg-gray-50 text-left text-xs text-gray-600 uppercase">
                    <tr>
                      <th className="px-3 py-2">Tier</th>
                      <th className="px-3 py-2">Min (AUD)</th>
                      <th className="px-3 py-2">Max (AUD)</th>
                      <th className="px-3 py-2">KYC required</th>
                      <th className="px-3 py-2">Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TIERS.map((tid) => {
                      const t = sharedPayoutPolicy.tiers[tid] || {}
                      const maxVal = t.maxAmount === null || t.maxAmount === '' ? '' : t.maxAmount
                      return (
                        <tr key={tid} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-medium text-gray-900">
                            <span className="font-mono text-teal-800">{tid}</span>
                            <span className="block text-xs font-normal text-gray-500">{TIER_ONBOARDING_LABELS[tid]}</span>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              className="input-field w-24"
                              value={t.minAmount ?? 0}
                              onChange={(e) =>
                                setSharedPayoutPolicy((s) => ({
                                  ...s,
                                  tiers: {
                                    ...s.tiers,
                                    [tid]: { ...s.tiers[tid], minAmount: Number(e.target.value) || 0 },
                                  },
                                }))
                              }
                            />
                          </td>
                          <td className="px-3 py-2">
                            <input
                              type="number"
                              className="input-field w-24"
                              placeholder="No max"
                              value={maxVal}
                              onChange={(e) => {
                                const v = e.target.value
                                setSharedPayoutPolicy((s) => ({
                                  ...s,
                                  tiers: {
                                    ...s.tiers,
                                    [tid]: {
                                      ...s.tiers[tid],
                                      maxAmount: v === '' ? null : Number(v) || 0,
                                    },
                                  },
                                }))
                              }}
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={!!t.kycRequired}
                              onChange={(e) =>
                                setSharedPayoutPolicy((s) => ({
                                  ...s,
                                  tiers: {
                                    ...s.tiers,
                                    [tid]: { ...s.tiers[tid], kycRequired: e.target.checked },
                                  },
                                }))
                              }
                            />
                          </td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                            {formatTierRange(t.minAmount ?? 0, t.maxAmount)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div id="policy-approvals">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Payout approval thresholds (AUD)</h3>
              <p className="text-xs text-gray-500 mb-3">Engine rules — same keys as Venue Configuration → Payout (group scope).</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(operationalConfig.payout).map(([key, val]) => (
                  <Field key={key} label={PAYOUT_LABELS[key] || formatSettingLabel(key)} unit={unitOf('payout', key)}>
                    <input
                      type="number"
                      className="input-field w-full"
                      value={val}
                      onChange={(e) =>
                        setOperationalConfig((c) => ({
                          ...c,
                          payout: { ...c.payout, [key]: e.target.value },
                        }))
                      }
                    />
                  </Field>
                ))}
              </div>
            </div>

            <div id="policy-kyc">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">KYC defaults</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="When to require KYC">
                  <SelectDropdown
                    className="w-full"
                    value={operationalConfig.kyc.kyc_mode}
                    onChange={(e) =>
                      setOperationalConfig((c) => ({
                        ...c,
                        kyc: { ...c.kyc, kyc_mode: e.target.value },
                      }))
                    }
                    options={KYC_MODE_OPTIONS}
                  />
                </Field>
                <Field label="ID expiry warning" unit="days">
                  <input
                    type="number"
                    className="input-field w-full"
                    value={operationalConfig.kyc.id_expiry_warning_days}
                    onChange={(e) =>
                      setOperationalConfig((c) => ({
                        ...c,
                        kyc: { ...c.kyc, id_expiry_warning_days: e.target.value },
                      }))
                    }
                  />
                </Field>
                <Field label="Re-verification period" unit="months">
                  <input
                    type="number"
                    className="input-field w-full"
                    value={operationalConfig.kyc.reverification_period_months}
                    onChange={(e) =>
                      setOperationalConfig((c) => ({
                        ...c,
                        kyc: { ...c.kyc, reverification_period_months: e.target.value },
                      }))
                    }
                  />
                </Field>
                <Field label="Max failed verification attempts" unit="count">
                  <input
                    type="number"
                    className="input-field w-full"
                    value={operationalConfig.kyc.max_failed_verification_attempts}
                    onChange={(e) =>
                      setOperationalConfig((c) => ({
                        ...c,
                        kyc: { ...c.kyc, max_failed_verification_attempts: e.target.value },
                      }))
                    }
                  />
                </Field>
                <Field label="Cooldown after failed" unit="hours">
                  <input
                    type="number"
                    className="input-field w-full"
                    value={operationalConfig.kyc.cooldown_after_failed_hours}
                    onChange={(e) =>
                      setOperationalConfig((c) => ({
                        ...c,
                        kyc: { ...c.kyc, cooldown_after_failed_hours: e.target.value },
                      }))
                    }
                  />
                </Field>
                <Field label="Desktop biometric capture">
                  <SelectDropdown
                    className="w-full"
                    value={operationalConfig.kyc.desktop_biometric_mode}
                    onChange={(e) =>
                      setOperationalConfig((c) => ({
                        ...c,
                        kyc: { ...c.kyc, desktop_biometric_mode: e.target.value },
                      }))
                    }
                    options={[
                      { value: 'required', label: 'Required — selfie mandatory' },
                      { value: 'optional', label: 'Optional — staff can press Skip' },
                      { value: 'hidden',   label: 'Hidden — no biometric step shown' },
                    ]}
                  />
                </Field>
                <Field label="Multiple identity documents" unit="on_off">
                  <SelectDropdown
                    className="w-full"
                    value={operationalConfig.kyc.multi_document_enabled}
                    onChange={(e) =>
                      setOperationalConfig((c) => ({
                        ...c,
                        kyc: { ...c.kyc, multi_document_enabled: e.target.value },
                      }))
                    }
                    options={[
                      { value: 'false', label: 'Single ID (front + back)' },
                      { value: 'true',  label: 'Multiple IDs (e.g. licence + Medicare)' },
                    ]}
                  />
                </Field>
              </div>
            </div>

            <div id="policy-retention">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Document &amp; image retention</h3>
              <p className="text-xs text-gray-500 mb-3">Captured ID/licence and case-evidence images are auto-purged after the retention period. AUSTRAC record-keeping minimum is 7 years (≈2555 days).</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field label="Retain ID image after OCR">
                  <SelectDropdown
                    className="w-full"
                    value={operationalConfig.document_processing.retain_license_image_after_ocr}
                    onChange={(e) =>
                      setOperationalConfig((c) => ({
                        ...c,
                        document_processing: { ...c.document_processing, retain_license_image_after_ocr: e.target.value },
                      }))
                    }
                    options={[{ value: 'true', label: 'Keep image' }, { value: 'false', label: 'Discard after OCR' }]}
                  />
                </Field>
                {Object.entries(DOC_PROCESSING_LABELS).map(([key, label]) => (
                  <Field key={key} label={label.replace(/\s*\(days\)$/, '')} unit={unitOf('document_processing', key)}>
                    <input
                      type="number"
                      className="input-field w-full"
                      value={operationalConfig.document_processing[key]}
                      onChange={(e) =>
                        setOperationalConfig((c) => ({
                          ...c,
                          document_processing: { ...c.document_processing, [key]: e.target.value },
                        }))
                      }
                    />
                  </Field>
                ))}
              </div>
            </div>

            <div id="policy-aml">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">AML reporting thresholds</h3>
              <p className="text-xs text-gray-500 mb-3">Tunable detection thresholds. The TTR cash threshold is regime-aware and set by AUSTRAC — change it via the regulatory-regime configuration, not here.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {Object.entries(AML_LABELS).map(([key, label]) => (
                  <Field key={key} label={label.replace(/\s*\((AUD|count)\)$/, '')} unit={unitOf('aml_thresholds', key)}>
                    <input
                      type="number"
                      className="input-field w-full"
                      value={operationalConfig.aml_thresholds[key]}
                      onChange={(e) =>
                        setOperationalConfig((c) => ({
                          ...c,
                          aml_thresholds: { ...c.aml_thresholds, [key]: e.target.value },
                        }))
                      }
                    />
                  </Field>
                ))}
              </div>
            </div>

            <div id="policy-risk-bands">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Risk score bands</h3>
              <p className="text-xs text-gray-500 mb-3">Cutoffs that decide critical / high / medium / low patron-risk badges. Higher numbers = safer; bands cascade downwards.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(RISK_LABELS).map(([key, label]) => (
                  <Field key={key} label={label} unit={unitOf('risk_scoring', key)}>
                    <input
                      type="number"
                      className="input-field w-full"
                      value={operationalConfig.risk_scoring[key]}
                      onChange={(e) =>
                        setOperationalConfig((c) => ({
                          ...c,
                          risk_scoring: { ...c.risk_scoring, [key]: e.target.value },
                        }))
                      }
                    />
                  </Field>
                ))}
              </div>
            </div>

            <div id="policy-alerts">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Alert cadence</h3>
              <p className="text-xs text-gray-500 mb-3">How quickly the same alert can re-fire for the same patron. Larger windows reduce noise; smaller windows raise sensitivity.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(ALERT_LABELS).map(([key, label]) => (
                  <Field key={key} label={label.replace(/\s*\(hours\)$/, '')} unit={unitOf('alert_policy', key)}>
                    <input
                      type="number"
                      className="input-field w-full"
                      value={operationalConfig.alert_policy[key]}
                      onChange={(e) =>
                        setOperationalConfig((c) => ({
                          ...c,
                          alert_policy: { ...c.alert_policy, [key]: e.target.value },
                        }))
                      }
                    />
                  </Field>
                ))}
              </div>
            </div>

            <div id="policy-co">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Reporting entity &amp; compliance officer</h3>
              <p className="text-xs text-gray-500 mb-3">Nominated officer who signs/submits SMRs. These flow into Part B of every AUSTRAC SMR submitted from this group.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(REPORTING_LABELS).map(([key, label]) => (
                  <Field key={key} label={label}>
                    <input
                      type={key.endsWith('_email') ? 'email' : 'text'}
                      className="input-field w-full"
                      value={operationalConfig.reporting[key] || ''}
                      onChange={(e) =>
                        setOperationalConfig((c) => ({
                          ...c,
                          reporting: { ...c.reporting, [key]: e.target.value },
                        }))
                      }
                    />
                  </Field>
                ))}
              </div>
            </div>

            <div id="policy-txn-risk">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Transaction risk scoring</h3>
              <p className="text-xs text-gray-500 mb-3">Per-payout heuristic shown in the Payout monitoring list. Each factor adds points; the total score drives the HIGH/MEDIUM/LOW badge and the "review recommended" flag. Lower cutoffs = more sensitive (more reviews).</p>
              <div className="space-y-4">
                {TXN_RISK_GROUPS.map((grp) => (
                  <div key={grp.title}>
                    <div className="text-xs font-semibold text-gray-700 mb-2">{grp.title}</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                      {grp.fields.map(([key, label]) => (
                        <Field key={key} label={label.replace(/\s*\((AUD|\$|%|score ≥|hours|days|months)\)$/, '').replace(/\s*\(score ≥\)$/, '')} unit={unitOf('transaction_risk', key)}>
                          <input
                            type="number"
                            className="input-field w-full"
                            value={operationalConfig.transaction_risk[key]}
                            onChange={(e) =>
                              setOperationalConfig((c) => ({
                                ...c,
                                transaction_risk: { ...c.transaction_risk, [key]: e.target.value },
                              }))
                            }
                          />
                        </Field>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div id="policy-patron-risk" className="border border-psg-teal/30 rounded-md p-3 bg-psg-teal/5 text-sm">
              <div className="font-semibold text-gray-900">Patron risk weightage</div>
              <p className="text-xs text-gray-600 mt-1 mb-2">The 7 risk-component weights (KYC, AML, behaviour, case history, SMR, ECDD, identity confidence) are tuned in a dedicated editor with sum-to-100 validation and live preview. Changes there immediately affect patron risk scoring.</p>
              <a href="/governance-safe/risk-config" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-psg-teal hover:underline">
                Open risk weightage editor →
              </a>
            </div>

            <div id="policy-modules">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Compliance modules</h3>
              <div className="space-y-2">
                {Object.entries(groupSettings.complianceSettings).map(([key, val]) => (
                  <label key={key} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!val}
                      onChange={(e) =>
                        setGroupSettings((g) => ({
                          ...g,
                          complianceSettings: { ...g.complianceSettings, [key]: e.target.checked },
                        }))
                      }
                    />
                    {formatSettingLabel(key)}
                  </label>
                ))}
              </div>
            </div>

            <div id="policy-reporting-scope">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Reporting scope</h3>
              <div className="space-y-2">
                {Object.entries(groupSettings.reportingConfig).map(([key, val]) => (
                  <label key={key} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!val}
                      onChange={(e) =>
                        setGroupSettings((g) => ({
                          ...g,
                          reportingConfig: { ...g.reportingConfig, [key]: e.target.checked },
                        }))
                      }
                    />
                    {formatSettingLabel(key)}
                  </label>
                ))}
              </div>
            </div>
            </div>
            <aside className="xl:w-52 xl:shrink-0 hidden xl:block">
              <nav className="sticky top-4 space-y-0.5 border-l border-gray-200 pl-3 text-xs">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-2">Jump to section</div>
                {[
                  ['policy-payout',           'Payout policy & tiers'],
                  ['policy-approvals',        'Payout approvals'],
                  ['policy-kyc',              'KYC defaults'],
                  ['policy-retention',        'Document retention'],
                  ['policy-aml',              'AML thresholds'],
                  ['policy-risk-bands',       'Risk score bands'],
                  ['policy-alerts',           'Alert cadence'],
                  ['policy-co',               'Compliance officer'],
                  ['policy-txn-risk',         'Transaction risk'],
                  ['policy-patron-risk',      'Patron risk weightage'],
                  ['policy-modules',          'Compliance modules'],
                  ['policy-reporting-scope',  'Reporting scope'],
                ].map(([anchor, label]) => (
                  <a key={anchor} href={`#${anchor}`} className="block px-2 py-1 rounded text-gray-700 hover:bg-gray-100 hover:text-gray-900">{label}</a>
                ))}
              </nav>
            </aside>
          </div>
        )}

        {step.id === 'egms' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Optional — add machines now or skip and use Group Configuration → EGMs later.</p>
            {egms.map((e, idx) => (
              <div key={idx} className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end border-b pb-3">
                <Field label="EGM ID">
                  <input className="input-field w-full" value={e.egm_id} onChange={(ev) => setEgms((arr) => { const n = [...arr]; n[idx] = { ...n[idx], egm_id: ev.target.value }; return n })} />
                </Field>
                <Field label="Venue">
                  <SelectDropdown
                    className="w-full"
                    value={e.venue_id}
                    onChange={(ev) => setEgms((arr) => { const n = [...arr]; n[idx] = { ...n[idx], venue_id: ev.target.value }; return n })}
                    options={[{ value: '', label: '—' }, ...venues.map((v) => ({ value: v.venue_id, label: v.name || v.venue_id }))]}
                  />
                </Field>
                <Field label="House no.">
                  <input className="input-field w-full" value={e.house_number} onChange={(ev) => setEgms((arr) => { const n = [...arr]; n[idx] = { ...n[idx], house_number: ev.target.value }; return n })} />
                </Field>
                <button type="button" className="text-sm text-red-600 mb-2" onClick={() => setEgms((arr) => arr.filter((_, i) => i !== idx))}>Remove</button>
              </div>
            ))}
            <button type="button" className="text-sm text-psg-teal font-medium" onClick={() => setEgms((arr) => [...arr, emptyEgm()])}>
              + Add EGM
            </button>
          </div>
        )}

        {step.id === 'review' && (
          <div className="space-y-4 text-sm">
            <div className="rounded-md bg-teal-50 border border-teal-100 p-4">
              <p className="font-medium text-teal-900">{business.legal_name || business.name}</p>
              <p className="text-teal-800">ABN {business.abn} · AUSTRAC {business.austrac_id || '—'} · {venues.length} venue(s)</p>
              <p className="text-teal-700 text-xs mt-2">
                PV1 {formatTierRange(sharedPayoutPolicy.tiers.PV1?.minAmount ?? 0, sharedPayoutPolicy.tiers.PV1?.maxAmount)} ·
                cash max ${Number(sharedPayoutPolicy.cashLimit || 0).toLocaleString()} ·
                SMR {groupSettings.reportingConfig?.smrReportingEnabled ? 'on' : 'off'}
              </p>
            </div>
            <ul className="space-y-2">
              {venues.map((v) => (
                <li key={v.venue_id} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={activateVenueIds.includes(v.venue_id)}
                    onChange={(e) => {
                      if (e.target.checked) setActivateVenueIds((ids) => [...new Set([...ids, v.venue_id])])
                      else setActivateVenueIds((ids) => ids.filter((id) => id !== v.venue_id))
                    }}
                  />
                  <span>
                    <span className="font-mono">{v.venue_id}</span> — {v.name}
                    {activateVenueIds.includes(v.venue_id) && (
                      <span className="ml-2 text-xs text-green-700">Active at go-live</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
            <p className="text-gray-500 text-xs">
              PaySafe will complete technical provisioning (hosting, PSG gateway, device approval) separately.
            </p>
          </div>
        )}
      </div>

      <div className="flex justify-between items-center">
        <button
          type="button"
          onClick={goBack}
          disabled={stepIndex === 0 || saving}
          className="inline-flex items-center gap-1 px-4 py-2 text-sm text-gray-700 border rounded-md disabled:opacity-40"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          Back
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={saving}
            className="px-4 py-2 text-sm text-gray-700 border rounded-md disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save draft'}
          </button>
          {step.id === 'review' ? (
            <button
              type="button"
              onClick={finish}
              disabled={saving}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-psg-teal text-white rounded-md disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Complete setup'}
              <CheckCircleIcon className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={goNext}
              disabled={saving}
              className="inline-flex items-center gap-1 px-4 py-2 text-sm bg-psg-teal text-white rounded-md disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Continue'}
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
        </div>
      </div>
    </div>
  )
}
